'use client';

import React from 'react';
import Link from 'next/link';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  Users,
  Newspaper,
  Binoculars
} from 'lucide-react';
import { TagMention } from '@/components/ui/TagMention';
import { MentionDedupeProvider } from '@/components/ui/MentionDedupe';
import { extractTwitterHandleFromHref } from '@/utils/markdown-utils';

interface EcosystemRecap {
  id: string;
  date: string;
  title: string;
  summary: string;
  content: string;
  highlights: string[];
  sentiment: 'bullish' | 'bearish' | 'neutral' | 'mixed';
  sourceCounts: {
    activityPulses: number;
    breakingNews: number;
    topTweets: number;
    handlesActive: number;
  };
  featuredImages?: string[]; // Images from tweets
  generatedAt: string;
  publishedAt?: string;
  model?: string;
  editedAt?: string;
  editedByName?: string;
}

interface HandleMapping {
  [handle: string]: { slug: string; name: string };
}

interface DailyRecapContentProps {
  latest: EcosystemRecap | null;
  recentRecaps: EcosystemRecap[];
  handleMap?: HandleMapping;
  /**
   * Present only on the per-chain reader (`/<hubSlug>/daily/<date>`). It re-points the
   * in-page links at that chain's own addresses so a reader inside the AVAX hub never gets
   * bounced to an Abstract recap by clicking "previous". ABSENT = the bare `/daily` reader,
   * whose links are byte-identical to what they have always been.
   */
  chainHub?: { hubSlug: string; label: string };
}

// Parse text for @mentions and $TOKEN references
function parseTextWithAllMentions(text: string): Array<{ type: 'text' | 'mention' | 'token'; value: string }> {
  if (!text) return [];
  const mentionRegex = /(@[a-zA-Z][a-zA-Z0-9_]*|\$[a-zA-Z][a-zA-Z0-9]*)/g;
  const segments: Array<{ type: 'text' | 'mention' | 'token'; value: string }> = [];
  let lastIndex = 0;

  let match;
  while ((match = mentionRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.substring(lastIndex, match.index) });
    }
    const matched = match[0];
    if (matched.startsWith('@')) {
      segments.push({ type: 'mention', value: matched.substring(1) });
    } else if (matched.startsWith('$')) {
      segments.push({ type: 'token', value: matched.substring(1) });
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.substring(lastIndex) });
  }
  return segments;
}

function TextWithMentions({ text, handleMap }: { text: string; handleMap?: HandleMapping }) {
  const segments = parseTextWithAllMentions(text);
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'mention' || segment.type === 'token') {
          const lowerValue = segment.value.toLowerCase();
          const prefix = segment.type === 'mention' ? '@' : '$';
          const mapping = handleMap?.[lowerValue];
          if (mapping) {
            return <TagMention key={`mention-${index}`} slug={mapping.slug} />;
          }
          return <React.Fragment key={index}>{prefix}{segment.value}</React.Fragment>;
        }
        return <React.Fragment key={index}>{segment.value}</React.Fragment>;
      })}
    </>
  );
}

function getSentimentIcon(sentiment: string, size: string = "w-5 h-5") {
  switch (sentiment) {
    case 'bullish': return <TrendingUp className={`${size} text-signal-green`} />;
    case 'bearish': return <TrendingDown className={`${size} text-alert-red`} />;
    case 'mixed': return <BarChart2 className={`${size} text-scout-gold`} />;
    default: return <Minus className={`${size} text-parchment-dim`} />;
  }
}

function getSentimentLabel(sentiment: string) {
  switch (sentiment) {
    case 'bullish': return { text: 'Bullish', color: 'text-signal-green' };
    case 'bearish': return { text: 'Bearish', color: 'text-alert-red' };
    case 'mixed': return { text: 'Mixed', color: 'text-scout-gold' };
    default: return { text: 'Neutral', color: 'text-parchment-dim' };
  }
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (dateString === today.toISOString().split('T')[0]) return "Today";
  if (dateString === yesterday.toISOString().split('T')[0]) return "Yesterday";

  // UTC-pinned: dateString is a date-only key (YYYY-MM-DD) that new Date() parses as
  // UTC midnight. Without timeZone:'UTC' a viewer west of UTC formats it as the PREVIOUS
  // day — both a wrong-date bug and a hydration mismatch vs the UTC-baked (ISR) server HTML.
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatFullDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export default function DailyRecapContent({ latest, recentRecaps, handleMap, chainHub }: DailyRecapContentProps) {
  // Link bases. Without chainHub these resolve to the exact strings used before.
  const recapBase = chainHub ? `/${chainHub.hubSlug}/daily` : '/daily';
  const archiveHref = chainHub ? `/${chainHub.hubSlug}/read` : '/daily/archive';
  const archiveLabel = chainHub ? 'The Wire' : 'All Recaps';
  const homeHref = chainHub ? `/${chainHub.hubSlug}/` : '/abstract/';
  if (!latest) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="font-display text-3xl font-bold text-parchment mb-3">No Recaps Yet</h1>
          <p className="text-parchment-dim mb-8 max-w-md mx-auto">
            Today's ecosystem updates are being compiled. Check back soon!
          </p>
          {/* Product surface — "home" = the desk (`/` is the marketing homepage now;
              the old `/`→`/abstract` 307 is gone). */}
          <Link
            href={homeHref}
            className="inline-flex items-center gap-2 px-5 py-2.5 border-[0.5px] border-scout-gold text-scout-gold rounded-lg hover:bg-scout-gold/10 transition-colors font-mono text-[11px] uppercase tracking-[0.14em]"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // Markdown components with enhanced readability
  const markdownComponents = {
    p: ({ children, ...props }: any) => {
      const processChildren = (child: any): any => {
        if (typeof child === 'string') return <TextWithMentions text={child} handleMap={handleMap} />;
        if (Array.isArray(child)) return child.map((c, i) => <React.Fragment key={i}>{processChildren(c)}</React.Fragment>);
        if (child?.props?.children) return React.cloneElement(child, { ...child.props, children: processChildren(child.props.children) });
        return child;
      };
      return <p className="mb-6" {...props}>{processChildren(children)}</p>;
    },
    h3: ({ children, ...props }: any) => (
      <h3 className="font-display text-lg font-bold text-parchment mt-12 mb-4 pb-3 border-b-[0.5px] border-divider" {...props}>{children}</h3>
    ),
    h4: ({ children, ...props }: any) => (
      <h4 className="font-display text-base font-bold text-parchment mt-8 mb-3" {...props}>{children}</h4>
    ),
    ul: ({ children, ...props }: any) => (
      <ul className="my-6 space-y-3 pl-0 list-none" {...props}>{children}</ul>
    ),
    li: ({ children, ...props }: any) => (
      <li className="flex items-start gap-3 text-parchment/90" {...props}>
        <span className="w-1.5 h-1.5 rounded-full bg-scout-gold mt-2.5 flex-shrink-0" />
        <span>{children}</span>
      </li>
    ),
    blockquote: ({ children, ...props }: any) => (
      <blockquote className="my-8 rounded-r-lg border-l-4 border-scout-gold bg-surface py-4 pl-6 italic text-parchment-dim" {...props}>{children}</blockquote>
    ),
    a: ({ href, children, ...props }: any) => {
      // Inline twitter/x.com profile links bypass the @mention path. When the
      // handle maps to a known entity, render the rich chip (de-duped to first
      // mention); otherwise leave it a plain external link.
      const twitterHandle = extractTwitterHandleFromHref(href);
      const mapping = twitterHandle ? handleMap?.[twitterHandle] : undefined;
      if (mapping) {
        return <TagMention slug={mapping.slug} />;
      }
      return (
        <a href={href} className="text-scout-gold underline transition-colors hover:text-scout-gold/80" target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined} {...props}>{children}</a>
      );
    },
    hr: () => (
      <div className="my-10 flex items-center justify-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-divider" />
        <span className="w-1.5 h-1.5 rounded-full bg-divider" />
        <span className="w-1.5 h-1.5 rounded-full bg-divider" />
      </div>
    ),
  };

  const sentiment = getSentimentLabel(latest.sentiment);

  return (
    <div className="py-8 lg:py-10">
      <MentionDedupeProvider>
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <header className="mb-10">
          {/* Nav */}
          <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.14em] text-parchment-dim">
            <Link
              href={archiveHref}
              className="inline-flex items-center gap-1.5 transition-colors hover:text-scout-gold"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              {archiveLabel}
            </Link>
            <span>·</span>
            <span className="text-scout-gold">Daily Recap</span>
            <span>·</span>
            <span>{formatFullDate(latest.date)}</span>
            <span>·</span>
            <span className={`inline-flex items-center gap-1 ${sentiment.color}`}>
              {getSentimentIcon(latest.sentiment, "w-3.5 h-3.5")}
              {sentiment.text}
            </span>
          </div>

          {/* Title */}
          <h1 className="mb-4 font-display text-3xl font-bold leading-[1.15] tracking-[-0.02em] text-parchment md:text-4xl lg:text-[2.75rem]">
            {latest.title}
          </h1>

          {/* Summary */}
          <p className="font-body text-lg leading-relaxed text-parchment-dim md:text-xl">
            <TextWithMentions text={latest.summary} handleMap={handleMap} />
          </p>
        </header>

        {/* Key Highlights */}
        {latest.highlights && latest.highlights.length > 0 && (
          <section className="mb-10 border-t-[0.5px] border-divider pt-8">
            <h2 className="mb-6 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-parchment-dim">
              Key Highlights
            </h2>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {latest.highlights.map((highlight, idx) => (
                <div
                  key={idx}
                  className="group flex items-start gap-4 rounded-xl border-[0.5px] border-divider bg-surface p-5 transition-colors hover:border-scout-gold/50"
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-scout-gold/10 font-mono text-sm font-bold text-scout-gold">
                    {idx + 1}
                  </span>
                  <p className="text-[15px] leading-relaxed text-parchment/90">
                    <TextWithMentions text={highlight} handleMap={handleMap} />
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Image Gallery - DISABLED for now
            Reason: Engagement-sorted images without context/attribution doesn't add value.
            Revisit when we have: manual curation, whitelist of trusted accounts, or AI quality filtering.
            See: AI_AUTOMATION_ROADMAP.md - Ecosystem Recap v2
        */}

        {/* Full Analysis */}
        <section className="border-t-[0.5px] border-divider pt-8">
          <h2 className="mb-8 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-parchment-dim">
            Full Analysis
          </h2>

          <div className="
            prose prose-invert prose-lg max-w-none break-words
            prose-p:text-parchment/90 prose-p:leading-[1.8] prose-p:mb-6 prose-p:font-body
            prose-li:text-parchment/90 prose-li:leading-[1.7]
            prose-ul:my-6 prose-ol:my-6 prose-ul:space-y-2 prose-ol:space-y-2
            prose-strong:text-parchment prose-strong:font-semibold
            prose-h3:font-display prose-h3:text-xl prose-h3:font-bold prose-h3:mt-12 prose-h3:mb-4
            prose-h3:text-parchment
            prose-h3:pb-3 prose-h3:border-b-[0.5px] prose-h3:border-divider
            [&>p:first-of-type]:text-xl [&>p:first-of-type]:text-parchment
            [&>p:first-of-type]:leading-relaxed [&>p:first-of-type]:font-medium
          ">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
            >
              {latest.content}
            </ReactMarkdown>
          </div>
        </section>

        {/* Sources & Meta */}
        <section className="mt-10 rounded-xl border-[0.5px] border-divider bg-surface px-5 py-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.12em] text-parchment-dim">
            <span className="inline-flex items-center gap-2">
              <Binoculars className="w-4 h-4 text-scout-gold" />
              Powered by RECON Intel
            </span>
            <span className="inline-flex items-center gap-2">
              <Users className="w-4 h-4" />
              {latest.sourceCounts?.handlesActive || latest.sourceCounts?.activityPulses || 0} ecosystem voices
            </span>
            {latest.editedAt && (
              <span className="inline-flex items-center gap-2">
                <Newspaper className="w-4 h-4" />
                Editor curated
              </span>
            )}
          </div>
        </section>

        {/* Previous Recaps */}
        {recentRecaps.length > 0 && (
          <section className="mt-10 border-t-[0.5px] border-divider pt-8">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-parchment-dim">
                Previous Recaps
              </h2>
              <Link
                href={archiveHref}
                className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.14em] text-scout-gold transition-colors hover:text-scout-gold/80"
              >
                View all
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {recentRecaps.slice(0, 3).map((recap) => {
                const recapSentiment = getSentimentLabel(recap.sentiment);
                return (
                  <Link
                    key={recap.id}
                    href={`${recapBase}/${recap.date}`}
                    className="group rounded-xl border-[0.5px] border-divider bg-surface p-5 transition-colors hover:border-scout-gold/50"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-parchment">
                        {formatDate(recap.date)}
                      </span>
                      <span className={`inline-flex items-center gap-1 ${recapSentiment.color}`}>
                        {getSentimentIcon(recap.sentiment, "w-3.5 h-3.5")}
                      </span>
                    </div>
                    <p className="line-clamp-3 text-sm text-parchment-dim transition-colors group-hover:text-parchment">
                      {recap.title}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
      </MentionDedupeProvider>
    </div>
  );
}
